import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core'
import { UntypedFormGroup } from '@angular/forms'
import { CartItemListComponent } from '@spartacus/cart/base/components'
import {
  ActiveCartFacade,
  CartItemComponentOptions,
  MultiCartFacade,
  OrderEntry,
  SelectiveCartFacade
} from '@spartacus/cart/base/root'
import { UserIdService } from '@spartacus/core'
import { Observable } from 'rxjs'
import { map, startWith, take, tap } from 'rxjs/operators'
import { EbAvailabilityItem } from '../../../product/interfaces/eb-availability.interface'
import {
  EbProductPrice,
  EbProductPriceItem
} from '../../../product/interfaces/eb-product.interface'
import { EbAvailabilityService } from '../../../product/services/facades/eb-availability.service'
import { EbPricesService } from '../../../product/services/facades/eb-prices.service'
import { EbOrderEntry } from '../../interface/eb-order-entry.interface'
@Component({
    selector: 'eb-cart-item-list',
    templateUrl: './eb-cart-item-list.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EbCartItemListComponent extends CartItemListComponent implements OnInit, OnDestroy {
  @Input() displayEntriesCompact = false
  @Input() controlled = true
  @Input() showPriceValue = true
  @Input() showAvailability = true
  @Input() readonly = false
  @Input() hasHeader = true
  @Input() loadPrices = true
  @Input() loadAvailabilities: boolean
  @Input() showAddToCart
  @Input() hideQuantity = false
  productPrices: Map<string, EbProductPrice> = new Map()
  productAvailabilities: Map<string, EbAvailabilityItem> = new Map()
  @Input() options: CartItemComponentOptions = {
    isSaveForLater: false,
    optionalBtn: null
  }

  @Input('items')
  set items(items: EbOrderEntry[]) {
    super.items = items
    if (this.loadPrices) {
      this.loadPricesForProducts()
    }
    if (this.loadAvailabilities) {
      this.loadAvailabilityForProducts()
    }
  }
  get items(): EbOrderEntry[] {
    return super.items as EbOrderEntry[]
  }

  @Input('cartIsLoading') set setLoading(value: boolean) {
    super.setLoading = value
  }

  constructor(
    activeCartService: ActiveCartFacade,
    selectiveCartService: SelectiveCartFacade,
    userIdService: UserIdService,
    multiCartService: MultiCartFacade,
    cd: ChangeDetectorRef,
    protected availabilityService: EbAvailabilityService,
    protected pricesService: EbPricesService
  ) {
    super(activeCartService, selectiveCartService, userIdService, multiCartService, cd)
  }

  ngOnInit(): void {
    super.ngOnInit()
  }

  protected getControlName(item: OrderEntry): string {
    return item.entryNumber?.toString()
  }

  loadPricesForProducts(orderEntries?: EbOrderEntry[]) {
    const orderEntriesToFetchPricesFor = orderEntries || this.items
    const ebProductPriceItems: EbProductPriceItem[] = orderEntriesToFetchPricesFor
      .filter(item => item.product?.orderable || item.product?.successor?.orderable)
      .map(item => ({
        product: item.product.code || item.product.successor?.code,
        quantity: 1
      }))
    if (ebProductPriceItems.length) {
      this.pricesService
        .fetchPrices(ebProductPriceItems)
        .pipe(take(1))
        .subscribe(price => {
          orderEntriesToFetchPricesFor.forEach(item => {
            const code = item.product.code || item.product.successor?.code
            this.productPrices.set(code, {
              ...price,
              items: [price?.items?.find(priceItem => priceItem.product === code)]
            })
          })
          this.cd.detectChanges()
        })
    }
  }

  loadAvailabilityForProducts(orderEntries?: EbOrderEntry[]) {
    const orderEntriesToFetchAvailabilityFor = orderEntries || this.items
    const ebProductAvailabilityItems: EbAvailabilityItem[] = orderEntriesToFetchAvailabilityFor
      .filter(item => item.product?.orderable)
      .map(item => ({
        product: item.product.code || item.product.successor?.code,
        quantity: 1
      }))
    if (ebProductAvailabilityItems.length) {
      this.availabilityService
        .fetchAvailabilities(ebProductAvailabilityItems, false)
        .pipe(take(1))
        .subscribe(availability => {
          orderEntriesToFetchAvailabilityFor.forEach(item => {
            this.productAvailabilities.set(item.product.code, {
              ...availability?.items?.find(
                availabilityItem => availabilityItem.product === item.product.code
              )
            })
          })
          this.cd.detectChanges()
        })
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy()
  }

  getControl(item: EbOrderEntry): Observable<UntypedFormGroup> {
    const minOrderQuantity = this.getMinOrderQuantity(item)
    return this.form.get(this.getControlName(item)).valueChanges.pipe(
      // eslint-disable-next-line import/no-deprecated
      startWith(null),
      tap(value => {
        if (item.updateable && value && !this.readonly) {
          if (this.selectiveCartService && this.options.isSaveForLater) {
            this.selectiveCartService.updateEntry(
              value.entryNumber,
              value.quantity * minOrderQuantity
            )
          } else if (this.cartId && this.userId) {
            this.multiCartService?.updateEntry(
              this.userId,
              this.cartId,
              value.entryNumber,
              value.quantity * minOrderQuantity
            )
          } else {
            this.activeCartService.updateEntry(value.entryNumber, value.quantity * minOrderQuantity)
          }
        }
      }),
      map(() => this.form.get(this.getControlName(item)) as UntypedFormGroup)
    )
  }

  getMinOrderQuantity(item: EbOrderEntry): number {
    const minOrderQuantity = item?.product?.productSalesView?.minOrderQuantity
    return minOrderQuantity || 1
  }
}
